import {Badge, Box, IconButton, AppBar, useTheme} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import {CaregiverMode, ScreenPaths, ThemeMode} from '../../../../enums';
import UserMenu from '../../../../components/user-menu/user-menu';
import {PendingAlert} from './pending-alert';
import {useAppContext} from '../../../../contexts/app/context';
import {useOffice} from '../../../../contexts/office/context';
import {useAuth} from '../../../../contexts/auth/context';
import {HeaderPicker} from '../../../../components/header-picker/header-picker';
import {translations} from '../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {ProductLogo} from '../../../../components/product-logo/product-logo';
import {useHistory} from 'react-router-dom';
import {VoximplantProcessState} from '../../../../contexts/office/types';

type OfficeHeaderProps = {
  runningMobile: boolean;
  headerHeight: number;
  isDrawerScrollVisible: boolean;
  onCall: () => void;
};

export const OfficeHeader = ({
  runningMobile,
  headerHeight,
  isDrawerScrollVisible,
  onCall,
}: OfficeHeaderProps) => {
  const theme = useTheme();
  const {t} = useTranslation();
  const appContext = useAppContext();
  const officeContext = useOffice();
  const {state: authState} = useAuth();
  let history = useHistory();

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme: any) => {
          if (runningMobile) {
            return 3;
          } else {
            return theme.zIndex.drawer + 1;
          }
        },
        height: `${headerHeight}px`,
        backgroundColor: theme.palette.background.default,
        backgroundImage: 'none',
        boxShadow: 'none',
        color: theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          paddingLeft: runningMobile ? '4px' : '16px',
          paddingRight: !isDrawerScrollVisible ? '16px' : '10px',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          backgroundColor:
            appContext.state.currentThemeMode === ThemeMode.Light
              ? theme.palette.background.paper
              : theme.palette.grey[900],
        }}
      >
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            color="inherit"
            onClick={() => {
              if (runningMobile) {
                // Toggle Drawer
                officeContext.toggleDrawer(!officeContext.state.drawerOpened);
              } else {
                // Toggle Collapse
                if (officeContext.state.drawerCollapeDisabled) {
                  // Disabled -> Enable it
                  officeContext.collapseDrawer(true, false);
                } else {
                  // Enabled -> Disable it
                  officeContext.collapseDrawer(false, true);
                }
              }
            }}
            edge="start"
            sx={{
              marginLeft: '0',
              marginRight: '0',
              color:
                appContext.state.currentThemeMode === ThemeMode.Light
                  ? theme.palette.primary.main
                  : '#fff',
              svg: {
                fontSize: '24px',
              },
            }}
          >
            <MenuRoundedIcon />
          </IconButton>
          {!runningMobile && (
            <Box
              sx={{
                paddingLeft: '8px',
                paddingRight: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <ProductLogo
                usePrimary={false}
                height={appContext.state.currentThemeSettings.homeLogoHeight}
              />
            </Box>
          )}
        </Box>
        {/* Affiliations picker */}
        {authState.session?.selectedAffiliation &&
          officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Professional && (
            <HeaderPicker
              id="affiliation-picker"
              type="affiliation"
              name={
                authState.session.selectedAffiliation.entity.shortName ||
                authState.session.selectedAffiliation.entity.name
              }
              picture={authState.session.selectedAffiliation.entity?.picture}
              caption={t(translations.office.professional_caregiver.affiliation_picker.caption)}
            />
          )}
        {/* Relationship picker */}
        {authState.session?.selectedRelationship &&
          officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Informal && (
            <HeaderPicker
              id="senior-picker"
              type="relationship"
              name={authState.session.selectedRelationship.withUser?.name}
              picture={authState.session.selectedRelationship.withUser?.picture}
              caption={t(translations.office.informal_caregiver.relationship_picker.caption)}
            />
          )}
        <Box sx={{flex: 1}}></Box>
        {/* Icons */}
        <Box sx={{display: 'flex'}}>
          {/* Calling */}
          {officeContext.state.currentCaregiverModeEnabled === CaregiverMode.Informal &&
            (() => {
              const isVoximplantReadyForOutgoingCalls =
                officeContext.state.voximplant.connectionState === VoximplantProcessState.Done &&
                officeContext.state.voximplant.signedInState === VoximplantProcessState.Done &&
                !officeContext.state.voximplant.degraded;

              const isDisabled =
                !authState.session?.selectedRelationship?.withUser?.id ||
                !isVoximplantReadyForOutgoingCalls;

              return (
                <IconButton disabled={isDisabled} onClick={onCall}>
                  <CallIcon
                    style={{
                      color: isDisabled ? 'gray' : theme.palette.primary.main,
                    }}
                  />
                </IconButton>
              );
            })()}
          {/* Notifications */}
          <IconButton
            onClick={() =>
              history.push(
                `${ScreenPaths.Office}${ScreenPaths.Office_Me}${ScreenPaths.Me_Notifications}`
              )
            }
          >
            <Badge
              overlap="circular"
              badgeContent={authState.session?.unreadNotificationsCount}
              color="error"
            >
              <NotificationsIcon
                style={{
                  fontSize: '28px',
                  color:
                    appContext.state.currentThemeMode === ThemeMode.Light
                      ? theme.palette.primary.main
                      : '#fff',
                }}
              />
            </Badge>
          </IconButton>
        </Box>
        {/* User menu */}
        {authState.firebaseUser && (
          <Box sx={{marginLeft: '10px'}}>
            <UserMenu />
          </Box>
        )}
      </Box>
      <PendingAlert />
    </AppBar>
  );
};
